import { SECTIONS } from '@/constants/navigation';
import { PermissionScope } from '@/services/AuthService';
import { Users, Smartphone, LayoutGrid, LucideIcon, InfoIcon, SettingsIcon, HelpCircle, FileTextIcon } from 'lucide-react';
import { PAGES } from '@/constants/pages';
import { RESOURCES } from '@/constants/resources';
import { ComponentType } from 'react';
import { EXTERNAL_LINKS } from '@/constants/externalLinks';
import { usePartner } from '@/contexts/partnerContext';

export interface NavItem {
  label: string;
  route?: string;
  link?: string;
  
  resource?: string | readonly string[];
  scope?: PermissionScope | readonly PermissionScope[];
}

export interface NavSection {
  header: string;
  icon?: LucideIcon;
  isCollapsible?: boolean;
  items?: NavItem[];
  component?: ComponentType;

  resource?: string | readonly string[];
  scope?: PermissionScope | readonly PermissionScope[];
  condition?: () => boolean; 
}

export const useNavConfig = () => {
  const { impersonatedPartnerId } = usePartner();

  const navConfig: readonly NavSection[] = [
    {
      header: SECTIONS.SOLOPROTECT_MANAGMENT,
      icon: LayoutGrid,
      items: [
        { label: PAGES.PARTNERS.title, route: PAGES.PARTNERS.route, resource: RESOURCES.PARTNERS, scope: PermissionScope.READ },
        { label: PAGES.BULK_UPLOAD_DEVICES.title, route: PAGES.BULK_UPLOAD_DEVICES.route, resource: RESOURCES.BULK_UPLOAD_DEVICES, scope: [PermissionScope.CREATE, PermissionScope.READ, PermissionScope.UPDATE, PermissionScope.DELETE ]},
        { label: PAGES.SOLOPROTECT_USERS.title, route: PAGES.SOLOPROTECT_USERS.route, resource: RESOURCES.INTERNAL_USERS, scope: [PermissionScope.CREATE, PermissionScope.READ, PermissionScope.UPDATE, PermissionScope.DELETE ]}
      ]
    },
    {
      header: SECTIONS.USER_MANAGEMENT,
      icon: Users,
      items: [
        { label: PAGES.USERS.title, route: PAGES.USERS.route, resource: [RESOURCES.PARTNER_USERS], scope: PermissionScope.READ }
      ],
      condition: () => impersonatedPartnerId != null
    },
    {
      header: SECTIONS.DEVICE_MANAGEMENT,
      icon: Smartphone,
      items: [
        { label: PAGES.DEVICES.title, route: PAGES.DEVICES.route, resource: RESOURCES.DEVICES, scope: PermissionScope.READ },
        { label: PAGES.CUSTOM_FIELDS.title, route: PAGES.CUSTOM_FIELDS.route, resource: RESOURCES.CUSTOM_FIELDS, scope: PermissionScope.READ },
        { label: PAGES.SIM_PROVIDERS.title, route: PAGES.SIM_PROVIDERS.route, resource: RESOURCES.SIM_PROVIDERS, scope: PermissionScope.READ },
        { label: PAGES.BULK_UPLOAD_SIMS.title, route: PAGES.BULK_UPLOAD_SIMS.route, resource: RESOURCES.BULK_UPLOAD_SIMS, scope: [PermissionScope.CREATE, PermissionScope.READ, PermissionScope.UPDATE, PermissionScope.DELETE ]}
      ],
      condition: () => impersonatedPartnerId != null
    },
    {
      header: SECTIONS.CONFIGURATION_MANAGEMENT,
      icon: SettingsIcon,
      items: [
        { label: PAGES.VIEW_CONFIGURATION.title, route: PAGES.VIEW_CONFIGURATION.route, resource: [RESOURCES.DEFAULT_CONFIG, RESOURCES.PARTNER_CONFIG], scope: PermissionScope.READ },
        { label: PAGES.CREATE_CONFIGURATION.title, route: PAGES.CREATE_CONFIGURATION.route, resource: [RESOURCES.DEFAULT_CONFIG, RESOURCES.PARTNER_CONFIG], scope: PermissionScope.CREATE }
      ],
      condition: () => impersonatedPartnerId != null
    },
    {
      header: SECTIONS.PLATFORM_AUDIT_LOGS,
      icon: InfoIcon,
      items: [
        { label: PAGES.PLATFORM_AUDIT_LOGS.title, route: PAGES.PLATFORM_AUDIT_LOGS.route, resource: RESOURCES.PLATFORM_AUDIT_LOGS, scope: PermissionScope.READ }
      ],
    },
    {
      header: SECTIONS.REPORTING,
      icon: FileTextIcon,
      items: [
        { label: PAGES.DEVICE_EXPORT.title, route: PAGES.DEVICE_EXPORT.route, resource: RESOURCES.PLATFORM_DEVICES, scope: PermissionScope.READ }
      ],
    },
    {
      header: SECTIONS.HELP_AND_SUPPORT,
      icon: HelpCircle,
      items: [
        { label: EXTERNAL_LINKS.PARTNER_RESOURCES.title, link: EXTERNAL_LINKS.PARTNER_RESOURCES.to }
      ],
      isCollapsible: false
    }
  ]

  return navConfig;
};

